import React from "react";
import intl from "react-intl-universal";
import { useParams, Redirect } from "react-router-dom";
import { useGet } from "restful-react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import AppBar from "@material-ui/core/AppBar";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import OndemandVideoIcon from "@material-ui/icons/OndemandVideo";
import BarChart from "@material-ui/icons/BarChart";
import { useHistory, Switch, Route } from "react-router-dom";
import { makeStyles, Theme } from "@material-ui/core/styles";

import { Heading } from "components/Heading";
import {
  IMatchWithballDataResponse,
  IWithballData,
  getMatchTeamWithballlData,
} from "apis";
import { getUsersFromWithballRawData } from "utils";
import { routing } from "routing";

import { TeamDataOverview } from "../TeamDataOverview";
import { PlayerDataView } from "../PlayerDataView";
import EventVideoView from "./EventVideoView/EventVideoView";

const TEAM_VALUE = "0";

const { Urls, getCurrentPath } = routing;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    width: "100%",
  },
}));

interface IMatchTeamStatsCompProps {
  matchId: string;
  teamId: string;
  playerId?: string;
}

const MatchTeamStatsComp = ({
  matchId,
  teamId,
  playerId,
}: IMatchTeamStatsCompProps) => {
  const classes = useStyles();

  const history = useHistory();
  const { data } = useGet<IMatchWithballDataResponse>({
    path: getMatchTeamWithballlData(matchId, teamId),
  });

  if (data) {
    data.withballData =
      data?.withballData.sort(
        (a: IWithballData, b: IWithballData) => a.created_at - b.created_at
      ) || [];
  }

  const players = getUsersFromWithballRawData(data?.withballData || [], true);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push(
      newValue === TEAM_VALUE
        ? Urls.MATCH_TEAM_STATS(matchId, teamId)
        : Urls.MATCH_TEAM_PLAYER_STATS(matchId, teamId, newValue)
    );
  };

  const currentId = playerId || TEAM_VALUE;

  if (
    currentId !== TEAM_VALUE &&
    players.length > 0 &&
    !players.find((player) => player.id.toString() === currentId)
  ) {
    return <Redirect to={Urls.MATCH_TEAM_STATS(matchId, teamId)} />;
  }

  const navToEventVideoPage = () => {
    history.push(getCurrentPath() + "/video");
  };

  const navToEventChartPage = () => {
    history.push(getCurrentPath().replace("/video", ""));
  };

  const renderNavChartButton = () => (
    <>
      <Button
        variant="contained"
        color="secondary"
        onClick={navToEventChartPage}
        startIcon={<BarChart />}
      >
        {intl.get("TEAM_DASHBOARD_NAV_EVENT_CHART")}
      </Button>
      <Box mt={2} />
    </>
  );

  return (
    <>
      <Heading title={intl.get("MATCH_TEAM_TITLE")} />
      <div className={classes.root}>
        <AppBar position="static">
          <Tabs
            value={currentId}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs example"
          >
            <Tab
              key={TEAM_VALUE}
              label={intl.get("MATCH_STATS_TEAM_OVERALL")}
              value={TEAM_VALUE}
            />
            {players.map((player) => (
              <Tab
                key={player.id}
                label={player.name || intl.get("MATCH_STATS_UNKNOWN_PLAYER")}
                value={player.id.toString()}
              />
            ))}
          </Tabs>
        </AppBar>
      </div>
      <Box mt={2} />
      <Switch>
        <Route exact path={Urls.MATCH_TEAM_PLAYER_STATS_VIDEO_PATTERN}>
          <>
            {renderNavChartButton()}
            <EventVideoView
              matchId={matchId}
              players={players}
              disablePlayerSelecter
              dataArray={data?.withballData || []}
            />
          </>
        </Route>
        <Route exact path={Urls.MATCH_TEAM_STATS_VIDEO_PATTERN}>
          <>
            {renderNavChartButton()}
            {data?.withballData && data.withballData.length > 0 && (
              <EventVideoView
                matchId={matchId}
                players={players}
                dataArray={data?.withballData}
              />
            )}
          </>
        </Route>
        <Route>
          <>
            <Button
              variant="contained"
              color="secondary"
              onClick={navToEventVideoPage}
              startIcon={<OndemandVideoIcon />}
            >
              {intl.get("TEAM_DASHBOARD_NAV_EVENT_ON_VIDEO")}
            </Button>
            <Box mt={2} />
            {currentId === TEAM_VALUE ? (
              <TeamDataOverview dataArray={data?.withballData || []} />
            ) : (
              <PlayerDataView
                dataArray={data?.withballData || []}
                playerId={currentId}
              />
            )}

            <Box mt={4} />
          </>
        </Route>
      </Switch>
    </>
  );
};

export const MatchTeamStats = () => {
  const { matchId, teamId, playerId } = useParams<{
    matchId: string;
    teamId: string;
    playerId: string;
  }>();
  if (!matchId || !teamId) {
    return null;
  }

  return (
    <MatchTeamStatsComp matchId={matchId} teamId={teamId} playerId={playerId} />
  );
};
